import { createSelector } from '@reduxjs/toolkit'
import Cookies from 'js-cookie'

import { getBookingFlowFromSearchParams } from '@/utils/bookingFlow'
import { SPIT_DEBUG_SP_KEY } from '@/utils/constants'
import { getSct } from '@/utils/getSct'
import { resolveChannelAndModeFromUrl } from '@/utils/resolveChannelAndModeFromUrl'
import { AppMode, Country } from '../models/app'

import { BookingFlow } from './bookingAvailabilitySlice'
import { resetAllState, setupByUrl } from './commonActions'
import { createAppSlice } from './createAppSlice'

const COUNTRY_COOKIE = 'spiagge-country'

export enum AppChannel {
  YOUR_BEACH = 'yb',
  APP_YOUR_BEACH = 'app-yb',
  KIOSK = 'kiosk',
  WIDGET = 'widget',
}

export interface AppSliceState {
  bookingFlow: null | BookingFlow
  channel: AppChannel | string
  country: Country
  debugMode: boolean
  mode: AppMode
  sct: number[] | null
  userData: {
    name: string | null
    surname: string | null
    email: string | null
    phone: string | null
  }
}

const initialState: AppSliceState = {
  bookingFlow: null,
  // channel is a free text string, so it can be anything
  // it's used to track the source of the user
  channel: 'yb',
  country: Country.IT,
  debugMode: false,
  // mode should be the kind of app the user is using
  mode: AppMode.WIDGET,
  sct: null,
  userData: {
    email: null,
    name: null,
    phone: null,
    surname: null,
  },
}

export const appSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
    builder.addCase(setupByUrl, (state, action) => {
      const url = new URL(action.payload)
      const channelAndModeResolution = resolveChannelAndModeFromUrl(
        action.payload
      )
      state.bookingFlow = getBookingFlowFromSearchParams(url.searchParams)

      state.debugMode = url.searchParams.get(SPIT_DEBUG_SP_KEY) === '1'

      const sct = getSct(action.payload)
      if (sct !== null) {
        state.sct = sct
      }
      state.userData = {
        email: url.searchParams.get('email'),
        name: url.searchParams.get('name'),
        phone: url.searchParams.get('phone'),
        surname: url.searchParams.get('surname'),
      }

      if (channelAndModeResolution !== null) {
        state.channel = channelAndModeResolution.channel
        state.mode = channelAndModeResolution.mode
      }

      const country = url.searchParams.get('country')
      if (country !== null) {
        switch (country) {
          case 'fr':
            state.country = Country.FR
            break

          default:
            state.country = Country.IT
            break
        }
      } else {
        // check cookies
        const countryCookie = Cookies.get(COUNTRY_COOKIE)
        if (typeof countryCookie === 'string') {
          state.country = countryCookie as Country
        }
      }

      Cookies.set(COUNTRY_COOKIE, state.country)
    })
  },
  initialState,
  name: 'app',
  reducers: (create) => ({
    setSct: create.reducer<number[] | null>((state, action) => {
      state.sct = action.payload
    }),
  }),
  selectors: {
    bookingFlow: (state) => state.bookingFlow,
    channel: (state) => state.channel,
    country: (state) => state.country,
    debugMode: (state) => state.debugMode,
    mode: (state) => state.mode,
    sct: (state) => state.sct,
    userData: (state) => state.userData,
  },
})

export const isAppSelector = createSelector(
  [appSlice.selectors.mode],
  (mode) => mode === AppMode.APP
)
export const isKioskSelector = createSelector(
  [appSlice.selectors.mode],
  (mode) => mode === AppMode.KIOSK
)
export const isWebsiteSelector = createSelector(
  [appSlice.selectors.mode],
  (mode) => mode === AppMode.WEBSITE
)
export const isWidgetSelector = createSelector(
  [appSlice.selectors.mode],
  (mode) => mode === AppMode.WIDGET
)
